import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export default function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await api.get('/api/keys');
      setKeys(res.data.keys);
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    try {
      const res = await api.post('/api/keys', { name: newKeyName || 'Untitled Key' });
      setGeneratedKey(res.data.api_key);
      setNewKeyName('');
      fetchKeys();
    } catch {
      // silent
    }
  };

  const revokeKey = async (id: number) => {
    try {
      await api.delete(`/api/keys/${id}`);
      setConfirmRevoke(null);
      fetchKeys();
    } catch {
      // silent
    }
  };

  const copyToClipboard = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">API Keys</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage API keys for programmatic access</p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setGeneratedKey(null); }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          + Generate Key
        </button>
      </div>

      {generatedKey && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
          <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">API Key Generated</p>
          <p className="text-xs text-green-700 dark:text-green-400 mb-3">Copy this key now. It will not be shown again.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 text-sm font-mono break-all text-gray-800 dark:text-gray-200">{generatedKey}</code>
            <button onClick={copyToClipboard} className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm">
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {showCreate && !generatedKey && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <p className="font-medium text-gray-800 dark:text-gray-100 mb-3">New API Key</p>
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Key name (e.g., Production, Dev)"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
          />
          <div className="flex gap-2">
            <button onClick={createKey} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
              Generate
            </button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="text-gray-500 dark:text-gray-400">Loading...</div></div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <p className="text-4xl mb-2">🔑</p>
          <p>No API keys yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map((key) => (
            <div key={key.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100">{key.name}</h3>
                  <p className="text-sm font-mono text-gray-500 dark:text-gray-400 mt-1">{key.prefix}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${key.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                    {key.is_active ? 'Active' : 'Revoked'}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                <span>Created {new Date(key.created_at).toLocaleDateString()}</span>
                <span>{key.last_used_at ? `Last used ${new Date(key.last_used_at).toLocaleDateString()}` : 'Never used'}</span>
              </div>
              {key.is_active && (
                <div className="mt-3">
                  {confirmRevoke === key.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-600 dark:text-red-400">Revoke this key?</span>
                      <button onClick={() => revokeKey(key.id)} className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                        Yes, Revoke
                      </button>
                      <button onClick={() => setConfirmRevoke(null)} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmRevoke(key.id)} className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                      Revoke
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
